import type { Uuid } from "spacetimedb";
import { Button } from "@/web/components/primitives/button";
import { DrawerBody, DrawerFooter } from "@/web/components/primitives/drawer";
import { toaster } from "@/web/components/primitives/toast";
import { ErrorHandler } from "@/web/lib/errors";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import type { PriceEstimateFeedback } from "./types";

export function PriceEstimateReview({
  propertyId,
  underwritableEntity,
  priceEstimate,
  selectedFeedback,
  onSelectFeedback,
  onComplete,
  isSubmitting,
  hasAppraisal,
}: {
  propertyId: Uuid;
  underwritableEntity: Uuid;
  priceEstimate: string;
  selectedFeedback: PriceEstimateFeedback | null;
  onSelectFeedback: (option: PriceEstimateFeedback) => void;
  onComplete: () => void;
  isSubmitting: boolean;
  hasAppraisal: boolean;
}) {
  const { mutate: orderAppraisal } = useProcedureMutation(procedures.requestAppraisalForProperty, {
    onError: ErrorHandler.default,
    onSuccess: () => {
      toaster({ message: "Appraisal request sent", variant: "success" });
    },
  });
  return (
    <>
      <DrawerBody className="flex min-h-0 flex-col px-5 pt-4 pb-0">
        <div className="border-2 border-withus-red px-5 py-7 text-center text-withus-red">
          <p className="text-sm font-medium">Price estimate</p>
          <p className="mt-6 font-heading text-4xl leading-none uppercase">{priceEstimate}</p>
        </div>

        <div className="mt-4 px-5 py-5 text-center text-withus-red">
          <p className="text-sm">What do you think about the price?</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {(["Too High", "Too Low", "Seems Right"] as const).map((option) => (
              <Button
                key={option}
                variant={selectedFeedback === option ? "solid" : "outline"}
                color="red"
                size="md"
                onClick={() => onSelectFeedback(option)}
              >
                {option}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4 border-2 border-withus-red px-4 py-4 text-withus-red">
          <p className="flex-1 text-sm font-medium">Want a more accurate price?</p>
          <Button
            color="red"
            size="md"
            disabled={hasAppraisal}
            onClick={() => {
              orderAppraisal({
                underwritableEntity: underwritableEntity,
                targetableProperty: propertyId,
              });
            }}
          >
            Order an appraisal
          </Button>
        </div>
      </DrawerBody>
      <DrawerFooter className="shrink-0 bg-withus-white pt-5">
        <Button
          color="red"
          size="lg"
          className="w-full"
          disabled={selectedFeedback == null || isSubmitting}
          isLoading={isSubmitting}
          onClick={onComplete}
        >
          Done
        </Button>
      </DrawerFooter>
    </>
  );
}
